/**
 * Closed package tab (acquisitions / postclose only).
 *
 * Structure per the Drilldown Tab Header Standard: one page header on the page
 * background, a KPI strip of bare tiles, then peer `LoanSectionCard`s. This tab
 * has NO page-level primary action — everything actionable here is scoped to one
 * card (populate the clause, validate it, flip a checkpoint), so the right side
 * of the header is deliberately empty rather than filled with a borrowed button.
 *
 * The three sections:
 *   • Closed package review  — note review, collateral, chain of assignment
 *   • Performance snapshot   — pay history and seasoning at the correspondent
 *   • Mortgagee clause       — populated from the closed docs, then validated
 */
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import GavelOutlined from "@mui/icons-material/GavelOutlined";
import HelpOutline from "@mui/icons-material/HelpOutline";
import VerifiedOutlined from "@mui/icons-material/VerifiedOutlined";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import LoanDrilldownTabPanel from "../LoanDrilldownTabPanel";
import LoanSectionCard from "../../shared/tabLayout/LoanSectionCard";
import LoanTabShell from "../../shared/tabLayout/LoanTabShell";
import SectionEmptyState from "../../shared/tabLayout/SectionEmptyState";
import { TabGhostButton, TabPrimaryButton } from "../../shared/tabLayout/tabButtons";
import { TAB_TOKENS as T } from "../../shared/tabLayout/tabTokens";
import ClosedPackageReviewBlock, {
  CLOSED_PACKAGE_CHECKS,
} from "./ClosedPackageReviewBlock";
import PerformanceSnapshotCard from "./PerformanceSnapshotCard";
import CollateralSearchAction from "../../components/CollateralSearchAction";
import { losAcquisitionsApi } from "../../../../api/losAcquisitionsApi";
import { loanDrilldownSet } from "../../../../redux/actions/loanDrilldownActions";
import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";

// MortgageeClause fields (loan-acquisitions.md §"Mortgagee clause"). Postclose
// POPULATES the clause from the closed docs (vs. preclose selecting a funding
// entity), then validates insurance/title reference it.
const MORTGAGEE_FIELDS = [
  { key: "name", label: "Mortgagee Name", full: true },
  { key: "addressLine1", label: "Address Line 1", full: true },
  { key: "addressLine2", label: "Address Line 2", full: true },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "zipCode", label: "Zip" },
  { key: "sourceDocumentId", label: "Source Document Id" },
  {
    key: "verbatimClause",
    label: "Verbatim Clause (as it appears in the closing docs)",
    full: true,
    multiline: true,
  },
];

const emptyForm = () => ({
  ...MORTGAGEE_FIELDS.reduce((a, f) => ({ ...a, [f.key]: "" }), {}),
  isaoaAtima: true,
});

const AcquisitionsClosedPackageTab = ({ value, tab }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const loan = useSelector((state) => state.loanDrilldown);
  const dealId = loan?._id;
  const mortgagee = loan?.mortgagee;
  const validated = loan?.mortgageeClauseValidated;
  const validationNotes = loan?.mortgageeClauseValidationNotes;

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [result, setResult] = useState(null); // last validate_mortgagee response

  const openForm = useCallback(() => {
    setForm({ ...emptyForm(), ...(mortgagee || {}) });
    setFormOpen(true);
  }, [mortgagee]);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const handleSave = () => {
    if (!dealId || saving) return;
    const payload = { isaoaAtima: !!form.isaoaAtima };
    MORTGAGEE_FIELDS.forEach((f) => {
      const v = (form[f.key] ?? "").trim?.() ?? form[f.key];
      if (v !== "" && v != null) payload[f.key] = v;
    });
    setSaving(true);
    losAcquisitionsApi
      .setMortgagee(dealId, payload)
      .then((updated) => {
        if (updated) dispatch(loanDrilldownSet(updated));
        enqueueSnackbar("Mortgagee clause saved", { variant: "success" });
        setFormOpen(false);
      })
      .catch((err) =>
        enqueueSnackbar(
          err?.response?.data?.detail || "Couldn't save mortgagee clause",
          { variant: "error" },
        ),
      )
      .finally(() => setSaving(false));
  };

  const handleValidate = () => {
    if (!dealId || validating) return;
    setValidating(true);
    // No body — the backend validates against the deal's insurance_policies.
    // (Extracted docs can be passed as { documents: [...] } later.)
    losAcquisitionsApi
      .validateMortgagee(dealId)
      .then((res) => {
        setResult(res);
        // Reflect the persisted flag locally without a full refetch.
        dispatch(
          loanDrilldownSet({
            ...loan,
            mortgageeClauseValidated: res?.validated,
            mortgageeClauseValidationNotes: res?.notes ?? null,
          }),
        );
        enqueueSnackbar(
          res?.validated
            ? "Mortgagee clause validated"
            : "Mortgagee clause did not fully validate",
          { variant: res?.validated ? "success" : "warning" },
        );
      })
      .catch((err) =>
        enqueueSnackbar(err?.response?.data?.detail || "Validation failed", {
          variant: "error",
        }),
      )
      .finally(() => setValidating(false));
  };

  const hasMortgagee = !!(
    mortgagee && (mortgagee.name || mortgagee.verbatimClause)
  );

  /* ── KPI strip ──
     Six figures that decide whether this file is buyable. Tones are reserved
     for the two that actually require action — if every tile is red, none of
     them reads as urgent. */
  const kpis = useMemo(() => {
    const checksDone = CLOSED_PACKAGE_CHECKS.filter(
      (c) => loan?.[c.key] === true,
    ).length;
    const checksTotal = CLOSED_PACKAGE_CHECKS.length;
    const noteStatus = loan?.noteReviewStatus;
    const clauseState = !hasMortgagee
      ? { value: null, tone: "neutral" }
      : validated === true
        ? { value: "Validated", tone: "ok" }
        : validated === false
          ? { value: "Not validated", tone: "danger" }
          : { value: "Unvalidated", tone: "warn" };

    return [
      {
        key: "checks",
        label: "Package checks",
        value: `${checksDone} of ${checksTotal}`,
        tone: checksDone === checksTotal ? "ok" : "warn",
        progress: checksTotal ? checksDone / checksTotal : 0,
      },
      {
        key: "note",
        label: "Note review",
        value: noteStatus,
        tone:
          noteStatus === "Failed"
            ? "danger"
            : noteStatus === "Exception"
              ? "warn"
              : "neutral",
      },
      {
        key: "clause",
        label: "Mortgagee clause",
        value: clauseState.value,
        tone: clauseState.tone,
      },
      {
        key: "asset",
        label: "Asset status",
        value: loan?.assetStatus,
      },
      {
        key: "payments",
        label: "Payments made",
        value: loan?.paymentsMade,
      },
      {
        key: "lpi",
        label: "Last paid installment",
        // formatDate returns the fallback for a missing date; pass "" so the
        // tile's own em-dash rule applies instead of a second convention.
        value: formatDate(loan?.lastPaidInstallmentDate, ""),
      },
    ];
  }, [loan, hasMortgagee, validated]);

  const clauseStatusChip = useMemo(() => {
    if (!hasMortgagee) return null;
    const style =
      validated === true
        ? { label: "Validated", color: T.color.okInk, background: T.color.okSoft }
        : validated === false
          ? {
              label: "Not validated",
              color: T.color.dangerInk,
              background: T.color.dangerSoft,
            }
          : {
              label: "Unvalidated",
              color: T.color.warnInk,
              background: T.color.warnSoft,
            };
    return (
      <Chip
        label={style.label}
        size="small"
        sx={{
          height: 21,
          fontSize: 11,
          fontWeight: 600,
          color: style.color,
          background: style.background,
        }}
      />
    );
  }, [hasMortgagee, validated]);

  return (
    <LoanDrilldownTabPanel value={value} tab={tab}>
      <LoanTabShell
        title="Closed package"
        subTitle="Diligence on a purchased loan: the paper behind it, how it performed, and the mortgagee clause insurance must reference."
        kpis={kpis}
      >
        {/* Existence/imaging check runs BEFORE the content review — a package
            that isn't at Sourcepoint and wasn't uploaded can't be reviewed. */}
        <CollateralSearchAction />
        <ClosedPackageReviewBlock />
        <PerformanceSnapshotCard />

        {/* ── Mortgagee clause ──
            Both controls are card-scoped (they act on this clause, not on the
            page), so they live in this card's header rather than the page
            header. */}
        <LoanSectionCard
          icon={<GavelOutlined />}
          title="Mortgagee clause"
          subTitle="Populated from the closed-loan package, then validated against the loan's insurance policies."
          controls={
            <Flx ac sx={{ gap: T.space.actionGap }}>
              {clauseStatusChip}
              <TabGhostButton
                size="sm"
                icon={<GavelOutlined />}
                label={hasMortgagee ? "Edit clause" : "Populate clause"}
                onClick={openForm}
              />
            </Flx>
          }
          disableBodyPadding={hasMortgagee}
        >
          {!hasMortgagee ? (
            <SectionEmptyState
              icon={<GavelOutlined />}
              message="Populate the clause from the closed docs to validate insurance and title against it."
              action={{
                label: "Populate clause",
                icon: <GavelOutlined />,
                onClick: openForm,
              }}
            />
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                "@media (max-width: 960px)": { gridTemplateColumns: "1fr" },
              }}
            >
              {/* ── Left: the clause as it reads on the closed docs ── */}
              <Box
                sx={{
                  padding: "18px",
                  borderRight: `1px solid ${T.color.border}`,
                  "@media (max-width: 960px)": {
                    borderRight: "none",
                    borderBottom: `1px solid ${T.color.border}`,
                  },
                }}
              >
                <Flx
                  column
                  sx={{
                    gap: "8px",
                    padding: "14px 16px",
                    background: T.color.surfaceSunk,
                    border: `1px solid ${T.color.border}`,
                    borderRadius: T.radius.sm,
                  }}
                >
                  <Flx ac sx={{ gap: "8px", flexWrap: "wrap" }}>
                    <Txt
                      sx={{ fontSize: 14, fontWeight: 700, color: T.color.ink }}
                    >
                      {mortgagee.name || "Mortgagee"}
                    </Txt>
                    {mortgagee.isaoaAtima && (
                      <Box
                        sx={{
                          fontSize: "9.5px",
                          fontWeight: 700,
                          letterSpacing: "0.04em",
                          color: T.color.accentInk,
                          background: T.color.brandSoft,
                          borderRadius: "5px",
                          padding: "2px 7px",
                        }}
                      >
                        ISAOA / ATIMA
                      </Box>
                    )}
                  </Flx>
                  {[
                    [mortgagee.addressLine1, mortgagee.addressLine2]
                      .filter(Boolean)
                      .join(", "),
                    [mortgagee.city, mortgagee.state, mortgagee.zipCode]
                      .filter(Boolean)
                      .join(", "),
                  ]
                    .filter(Boolean)
                    .map((line, i) => (
                      <Txt
                        key={i}
                        sx={{ fontSize: 12.5, color: T.color.ink2 }}
                      >
                        {line}
                      </Txt>
                    ))}
                  {mortgagee.verbatimClause && (
                    <Box
                      sx={{
                        marginTop: "3px",
                        paddingTop: "10px",
                        borderTop: `1px dashed ${T.color.borderStrong}`,
                        fontSize: 12,
                        fontStyle: "italic",
                        color: T.color.ink2,
                        whiteSpace: "pre-wrap",
                        lineHeight: 1.6,
                      }}
                    >
                      {mortgagee.verbatimClause}
                    </Box>
                  )}
                  {mortgagee.sourceDocumentId && (
                    <Txt sx={{ ...T.type.meta, marginTop: "4px" }}>
                      Populated from {mortgagee.sourceDocumentId}
                    </Txt>
                  )}
                </Flx>
              </Box>

              {/* ── Right: validation against insurance / title ── */}
              <Box sx={{ padding: "18px" }}>
                <Flx ac jb sx={{ marginBottom: "12px", gap: "10px" }}>
                  <Flx
                    ac
                    sx={{
                      gap: "7px",
                      fontSize: 12.5,
                      fontWeight: 700,
                      color:
                        validated === true
                          ? T.color.okInk
                          : validated === false
                            ? T.color.dangerInk
                            : T.color.muted,
                    }}
                  >
                    <Box
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: "99px",
                        background: "currentColor",
                      }}
                    />
                    {validated === true
                      ? "Validated"
                      : validated === false
                        ? "Not validated"
                        : "Not yet validated"}
                  </Flx>
                  <TabGhostButton
                    size="sm"
                    icon={<VerifiedOutlined />}
                    label={
                      result || validated != null ? "Re-validate" : "Validate"
                    }
                    loading={validating}
                    onClick={handleValidate}
                  />
                </Flx>

                {/* Per-document checks from the last validate call. */}
                {result?.checks?.length > 0 &&
                  result.checks.map((c, i) => (
                    <Flx
                      key={c.policyId || c.sourceDocumentId || i}
                      ac
                      sx={{
                        gap: "11px",
                        paddingBlock: "9px",
                        borderBottom: `1px solid ${T.color.border}`,
                        "&:last-of-type": { borderBottom: "none" },
                      }}
                    >
                      {c.matches ? (
                        <CheckCircleOutline
                          sx={{ fontSize: 18, color: T.color.okInk, flexShrink: 0 }}
                        />
                      ) : (
                        <ErrorOutline
                          sx={{ fontSize: 18, color: T.color.dangerInk, flexShrink: 0 }}
                        />
                      )}
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Txt sx={T.type.fieldValue}>
                          {c.coverageType
                            ? `${c.source} — ${c.coverageType}`
                            : c.source}
                        </Txt>
                        <Txt sx={{ ...T.type.meta, overflowWrap: "anywhere" }}>
                          {c.mortgageeFound
                            ? `Found: ${c.mortgageeFound}`
                            : "No mortgagee found on this document"}
                        </Txt>
                      </Box>
                      <Chip
                        label={c.matches ? "Match" : "Mismatch"}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: 10.5,
                          fontWeight: 700,
                          flexShrink: 0,
                          color: c.matches ? T.color.okInk : T.color.dangerInk,
                          background: c.matches
                            ? T.color.okSoft
                            : T.color.dangerSoft,
                        }}
                      />
                    </Flx>
                  ))}

                {result && !result.checks?.length && (
                  <Flx ac sx={{ gap: "8px", color: T.color.warnInk }}>
                    <HelpOutline sx={{ fontSize: 18 }} />
                    <Txt sx={{ fontSize: "12.5px", color: "inherit" }}>
                      No insurance or docs available to validate against yet.
                    </Txt>
                  </Flx>
                )}

                {!result && validated == null && (
                  <Txt sx={{ ...T.type.meta, fontSize: "12px" }}>
                    Run a validation to check the loan's insurance and title
                    reference this clause.
                  </Txt>
                )}

                {validationNotes && (
                  <Box
                    sx={{
                      marginTop: "12px",
                      padding: "9px 12px",
                      borderRadius: T.radius.sm,
                      fontSize: "12px",
                      lineHeight: 1.55,
                      color: validated ? T.color.okInk : T.color.warnInk,
                      background: validated ? T.color.okSoft : T.color.warnSoft,
                      border: `1px solid ${validated ? T.color.okLine : T.color.warnLine}`,
                    }}
                  >
                    {validationNotes}
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </LoanSectionCard>
      </LoanTabShell>

      {/* Populate / edit dialog */}
      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>
          {hasMortgagee ? "Edit mortgagee clause" : "Populate mortgagee clause"}
        </DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1.5,
              pt: 0.5,
            }}
          >
            {MORTGAGEE_FIELDS.map((f) => (
              <TextField
                key={f.key}
                label={f.label}
                value={form[f.key] ?? ""}
                onChange={(e) => set(f.key, e.target.value)}
                size="small"
                multiline={f.multiline}
                minRows={f.multiline ? 3 : undefined}
                sx={{ gridColumn: f.full ? "1 / -1" : "auto" }}
              />
            ))}
            <FormControlLabel
              sx={{ gridColumn: "1 / -1" }}
              control={
                <Switch
                  checked={!!form.isaoaAtima}
                  onChange={(e) => set("isaoaAtima", e.target.checked)}
                />
              }
              label="ISAOA / ATIMA"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setFormOpen(false)}
          >
            Cancel
          </Button>
          <TabPrimaryButton
            label="Save clause"
            onClick={handleSave}
            loading={saving}
            disabled={!(form.name?.trim() || form.verbatimClause?.trim())}
          />
        </DialogActions>
      </Dialog>
    </LoanDrilldownTabPanel>
  );
};

export default AcquisitionsClosedPackageTab;
